import { Component, Inject, ViewChild } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { AlertInfo, AlertType, AlertService } from '../../Services/Alert/alert.service';
import { Subject } from 'rxjs';
import { FileConst } from '../../Models/file-size.cont';
import { AlertComponent } from '../alert/alert.component';

@Component({
  standalone: false,
  selector: 'app-upload-files-dialog',
  templateUrl: './upload-files-dialog.component.html',
  styleUrls: ['./upload-files-dialog.component.scss'],
})
export class UploadFilesDialogComponent {
  filesToUpload: any[] = [];
  private _totalFilesSize: number = 0;
  saveDisabled: boolean = true;
  totalFilesSizeStr: string = '';
  fileCount = 0;

  constructor(
    public dialogRef: MatDialogRef<UploadFilesDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: UploadFilesDialogData,
    private alertService: AlertService
  ) {}

  get totalFilesSize(): number {
    return this._totalFilesSize;
  }
  set totalFilesSize(val: number) {
    this._totalFilesSize = val;
    this.totalFilesSizeStr = this.getFileSizeString(val);
    this.saveDisabled = this.totalFilesSize > this.data.maxUploadSize;
  }

  handleFileInput(files: FileList) {
    this.alertService.setAlert(null);
    this.saveDisabled = this.fileCount < 1;

    if (this.data.maxFileCount == 1 && this.fileCount == 1) {
      this.filesToUpload.splice(0, 1);
      this.fileCount = 0;
      this.totalFilesSize = 0;
    }

    for (let i = 0; i < files.length; i++) {
      if (!this.uploadFile(files[i])) break;
    }

    this.saveDisabled = this.fileCount < 1;
  }

  uploadFile(file: File): boolean {
    var name = file.name;
    var type = file.type;
    var size = file.size;
    var context = this;
    let reader = new FileReader();

    reader.onload = function () {
      context.filesToUpload.push({
        fileName: name,
        fileType: type,
        fileSize: size,
        fileContent: reader.result,
      });
    };

    if (file.size > this.data.maxFileSize) {
      this.alertService.setAlert(<AlertInfo>{
        title: `Cannot add file over ${this.data.displayMaxFileSize}`,
        message: `${file.name} is ${this.getFileSizeString(file.size)}`,
        type: AlertType.Error,
        closeable: true,
        targetId: 'uploadFileDlg',
      });
      return false;
    }

    if (this.fileCount >= this.data.maxFileCount) {
      this.alertService.setAlert(<AlertInfo>{
        title: `The maximum number of uploaded files is ${this.data.maxFileCount}`,
        message: '',
        type: AlertType.Error,
        closeable: true,
        targetId: 'uploadFileDlg',
      });
      return false;
    }

    this.fileCount++;
    this.totalFilesSize += file.size;

    if (this.data.readAsText) {
      reader.readAsText(file);
    } else {
      reader.readAsDataURL(file);
    }

    return true;
  }

  getFileSizeString(fileSize: number): string {
    if (fileSize < FileConst.KiloByte)
      //1 KB
      return fileSize + 'B';
    if (fileSize < FileConst.MegaByte)
      // <1MB && >1KB
      return this.round(fileSize / FileConst.KiloByte, 1) + 'KB';

    return this.round(fileSize / FileConst.MegaByte, 1) + 'MB';
  }

  round(value: number, precision: number): number {
    var multiplier = Math.pow(10, precision || 0);
    return Math.round(value * multiplier) / multiplier;
  }

  get toolTip() {
    if (this.data.maxFileCount > 1) {
      return `Max total file size: ${this.data.displayMaxUploadSize}; max individual file size: ${this.data.displayMaxFileSize}; max ${this.data.maxFileCount} files`;
    } else {
      return `Max file size: ${this.data.displayMaxFileSize}`;
    }
  }

  get acceptedExtensions() {
    return this.data.acceptedExtensions;
  }

  onNoClick(): void {
    this.dialogRef.close();
  }

  onYesClick(): void {
    this.dialogRef.close({ files: this.filesToUpload });
  }

  removeFile(i) {
    if (this.filesToUpload == null || this.filesToUpload.length <= i) return;

    this.filesToUpload.splice(i, 1);
    this.saveDisabled = --this.fileCount < 1;
  }
}

export class UploadFilesDialogData {
  constructor(
    public title: string,
    public acceptedExtensions: string,
    public maxFileSize: number,
    public displayMaxFileSize: string,
    public maxUploadSize: number,
    public displayMaxUploadSize: string,
    public readAsText?: boolean,
    public maxFileCount?: number
  ) {
    if (readAsText == null) this.readAsText = true;

    if (maxFileCount == null) this.maxFileCount = 1;
    else if (maxFileCount < 1) throw `Invalid maxFileCount {maxFileCount}`;
  }
}
